import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { useRaidContext } from '../../context/RaidContext';

import GeneralIssuesFilters from './GeneralIssuesFilters';
import GeneralIssuesTable from './GeneralIssuesTable';
import GeneralIssueUpsertModal from './GeneralIssueUpsertModal';

import Button from 'CommonComponents/Button';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';

export const UPSERT_ISSUE_MODAL_ID = 'upsert-general-issue-modal';

export default function GeneralIssuesIndex({ handleCreated }) {
    const [isLoading, setIsLoading] = useState(false);

    const {
        currentVenue,
        isMasterSource,
        risks,
        issues,
        decisions,
        impacts,
        venuesOptions,
        impactCategories,
        statuses,
        canManageIssues,
        fetchGeneralIssues,
        routes,
        getQuery,
        modalsVisibility,
        toggleModalVisibility,
        loadingDatasets,
        selectedIssue,
        setSelectedIssue,
    } = useRaidContext();

    const openIssues = useMemo(() => issues.filter((issue) => !issue.is_closed), [issues]);
    const closedIssues = useMemo(() => issues.filter((issue) => issue.is_closed), [issues]);

    const issueModal = useModal();

    const confirm = useConfirmationModal();

    useEffect(() => {
        setIsLoading(true);
        fetchGeneralIssues()
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        const currentId = getQuery().type === 'issue' ? getQuery().id : null;
        if (currentId) {
            const issue = issues.find((i) => i.id === parseInt(currentId));
            if (issue) {
                setSelectedIssue(issue);
                toggleModalVisibility(UPSERT_ISSUE_MODAL_ID, true);
            }
        }
    }, [issues]);

    useEffect(() => {
        if (modalsVisibility[UPSERT_ISSUE_MODAL_ID]) {
            issueModal.show();
        } else {
            issueModal.hide();
        }
    }, [modalsVisibility[UPSERT_ISSUE_MODAL_ID]]);

    const pushUrlToOriginal = () => {
        const query = getQuery();
        if (query.type && history.pushState) {
            const newurl = window.location.href.split('?')[0];
            window.history.pushState({ path: newurl }, '', newurl);
        }
    };

    const onCreated = () => {
        pushUrlToOriginal();
        fetchGeneralIssues();

        if (handleCreated && typeof handleCreated === 'function') {
            handleCreated();
        }
    };

    const onClose = () => {
        toggleModalVisibility(UPSERT_ISSUE_MODAL_ID, false);
    };

    const onEdit = (model) => {
        if (!canManageIssues) {
            return;
        }
        setSelectedIssue(model);
        toggleModalVisibility(UPSERT_ISSUE_MODAL_ID, true);
    };

    const deleteIssue = (model) => {
        const deleteUrl = routes.deleteGeneralIssueUrl.replace('GENERAL_ISSUE_ID', model.id);

        axios
            .delete(deleteUrl)
            .then((res) => {
                fetchGeneralIssues();
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const onDelete = (model) => {
        if (!canManageIssues) {
            return;
        }

        confirm({
            title: $t('Delete Issue'),
            message: $t('Are you sure you want to delete this?'),
            onConfirm: () => {
                deleteIssue(model);
            },
        });
    };

    const onStar = (model) => {
        if (!canManageIssues) {
            return;
        }
        const url = routes.updateGeneralIssueHotStatusUrl.replace('GENERAL_ISSUE_ID', model.id);
        axios.put(url, { is_hot: !model.is_hot }).then(() => fetchGeneralIssues());
    };

    return (
        <Fragment>
            <div className="table__card tw-space-y-8">
                <div>
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                        <div>
                            <h4 className="table__header">{$t('Issues')}</h4>
                        </div>
                        <div className="tw-flex tw-gap-x-1.5">
                            <Button variant="primary" as="a" href={routes.generalRisksExportUrl}>
                                {$t('Download')}
                            </Button>
                            <GeneralIssuesFilters
                                datasets={{ venuesOptions }}
                                showSourceFilter={isMasterSource}
                                onSubmit={(values) => fetchGeneralIssues(values)}
                                onReset={() => fetchGeneralIssues()}
                            />
                        </div>
                    </div>
                    <GeneralIssuesTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        issues={openIssues}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralIssueUrl}
                        reorderUrl={routes.reorderGeneralIssueUrl}
                        onStar={(issue) => onStar(issue)}
                        onEdit={(issue) => onEdit(issue)}
                        onDelete={(issue) => onDelete(issue)}
                        canManage={canManageIssues}
                    />
                </div>
                <div>
                    <h4 className="table__header tw-mb-6">{$t('Closed Issues')}</h4>
                    <GeneralIssuesTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        issues={closedIssues}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralIssueUrl}
                        reorderUrl={routes.reorderGeneralIssueUrl}
                        onStar={(issue) => onStar(issue)}
                        onEdit={(issue) => onEdit(issue)}
                        onDelete={(issue) => onDelete(issue)}
                        canManage={canManageIssues}
                    />
                </div>
            </div>

            <GeneralIssueUpsertModal
                id={UPSERT_ISSUE_MODAL_ID}
                currentVenue={currentVenue}
                isLoading={loadingDatasets}
                allowVenueSelection={isMasterSource}
                datasets={{ impacts, impactCategories, statuses, risks, decisions, venuesOptions }}
                storeUrl={routes.storeGeneralIssueUrl}
                updateUrl={routes.updateGeneralIssueUrl}
                modal={issueModal}
                issue={selectedIssue}
                onCreated={() => onCreated()}
                onClose={() => onClose()}
                canManage={canManageIssues}
            />
        </Fragment>
    );
}
